import React, { useEffect } from 'react';
import { useTranslation } from '../../i18n';
import '../../App.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  overlayClassName?: string;
  contentClassName?: string;
  overlayStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  overlayClassName,
  contentClassName,
  overlayStyle,
  contentStyle,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`modal-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
      style={overlayStyle}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        className={`modal-content${contentClassName ? ` ${contentClassName}` : ''}`}
        style={contentStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} className="modal-close-btn" aria-label={t('common.close')}>
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

export default Modal;
